import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Send, Trash2, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminSectionNav from "@/components/admin/AdminSectionNav";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { clearAdminSession, getStoredAdmin, hasAdminSession } from "@/lib/adminAuth";
import { newsletterApi } from "@/lib/newsletterApi";

const emptyForm = {
  title: "",
  excerpt: "",
  content: "",
  category: "Growth",
  authorName: "Grawish Editorial",
  tags: "",
  highlights: "",
  coverImage: "",
  isPublished: true,
  isFeatured: false,
  notifySubscribers: true,
};

const AdminNewsletterPage = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [admin, setAdmin] = useState(() => getStoredAdmin());
  const [form, setForm] = useState(emptyForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadDashboard = async () => {
    const [newsletterItems, subscriberItems] = await Promise.all([
      newsletterApi.getAdminNewsletters(),
      newsletterApi.getSubscribers(),
    ]);

    setNewsletters(newsletterItems);
    setSubscribers(subscriberItems);
  };

  useEffect(() => {
    if (!hasAdminSession()) {
      navigate("/admin/login");
      return;
    }

    loadDashboard().catch((error) => {
      if (error.response?.status === 401) {
        clearAdminSession();
        navigate("/admin/login");
        return;
      }
      setStatusMessage(error.response?.data?.message || "Unable to load admin data.");
    });
    newsletterApi
      .getCurrentAdmin()
      .then((data) => setAdmin(data.admin))
      .catch(() => {
        clearAdminSession();
        navigate("/admin/login");
      });
  }, [navigate]);

  const featuredCount = useMemo(
    () => newsletters.filter((newsletter) => newsletter.isFeatured).length,
    [newsletters]
  );

  const buildPayload = () => {
    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      payload.append(key, value);
    });

    if (selectedFile) {
      payload.append("coverImage", selectedFile);
    }

    return payload;
  };

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedFile(null);
    setEditingId("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      const payload = buildPayload();

      if (editingId) {
        await newsletterApi.updateNewsletter(editingId, payload);
        setStatusMessage("Newsletter updated successfully.");
      } else {
        await newsletterApi.createNewsletter(payload);
        setStatusMessage("Newsletter created successfully.");
      }

      resetForm();
      await loadDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.message || "Unable to save newsletter.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (newsletter) => {
    setEditingId(newsletter._id);
    setSelectedFile(null);
    setForm({
      title: newsletter.title,
      excerpt: newsletter.excerpt,
      content: newsletter.content,
      category: newsletter.category,
      authorName: newsletter.authorName,
      tags: (newsletter.tags || []).join(", "),
      highlights: (newsletter.highlights || []).join(", "),
      isPublished: newsletter.isPublished,
      isFeatured: newsletter.isFeatured,
      notifySubscribers: false,
      coverImage: newsletter.coverImage || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteNewsletter = async (id) => {
    try {
      await newsletterApi.deleteNewsletter(id);
      setStatusMessage("Newsletter deleted.");
      await loadDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.message || "Unable to delete newsletter.");
    }
  };

  const handleDeleteSubscriber = async (id) => {
    try {
      await newsletterApi.deleteSubscriber(id);
      setStatusMessage("Subscriber removed.");
      await loadDashboard();
    } catch (error) {
      setStatusMessage(error.response?.data?.message || "Unable to remove subscriber.");
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onContactClick={() => window.location.assign("/")} />

      <section className="pt-24">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid gap-5 lg:grid-cols-[1.3fr_0.7fr] lg:items-start">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">Newsletter Admin</h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-600">Manage newsletters and subscribers.</p>
              <AdminSectionNav admin={admin} onLogout={handleLogout} />
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { label: "Total newsletters", value: newsletters.length, icon: Plus },
                { label: "Featured stories", value: featuredCount, icon: Send },
                { label: "Subscribers", value: subscribers.length, icon: Users },
              ].map((item) => (
                <div key={item.label} className="rounded border border-gray-300 bg-white p-4 text-gray-800">
                  <item.icon className="h-4 w-4 text-gray-500" />
                  <div className="mt-3 text-2xl font-semibold">{item.value}</div>
                  <div className="mt-1 text-sm text-gray-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]">
          <form onSubmit={handleSubmit} className="rounded border border-gray-300 bg-white p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{editingId ? "Edit Newsletter" : "Create Newsletter"}</h2>
              </div>
              {editingId ? (
                <button type="button" onClick={resetForm} className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700">
                  Cancel
                </button>
              ) : null}
            </div>

            <div className="mt-5 space-y-3.5">
              <input
                required
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Newsletter title"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              />
              <textarea
                required
                rows={2}
                value={form.excerpt}
                onChange={(event) => setForm((current) => ({ ...current, excerpt: event.target.value }))}
                placeholder="Short summary"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              />
              <textarea
                required
                rows={7}
                value={form.content}
                onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
                placeholder="Full newsletter content. Use blank lines between paragraphs."
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
              />

              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={form.category}
                  onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                  placeholder="Category"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
                />
                <input
                  value={form.authorName}
                  onChange={(event) => setForm((current) => ({ ...current, authorName: event.target.value }))}
                  placeholder="Author name"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
                />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <input
                  value={form.tags}
                  onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))}
                  placeholder="Tags, comma separated"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
                />
                <input
                  value={form.highlights}
                  onChange={(event) => setForm((current) => ({ ...current, highlights: event.target.value }))}
                  placeholder="Highlights, comma separated"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500"
                />
              </div>

              <div className="rounded border border-gray-300 bg-gray-50 p-4">
                <label className="block text-sm font-semibold text-gray-700">Cover image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                  className="mt-2 block w-full text-sm text-gray-600"
                />
                {form.coverImage && !selectedFile ? (
                  <p className="mt-2 text-xs text-gray-500">Using existing uploaded image.</p>
                ) : null}
              </div>

              <div className="grid gap-2 md:grid-cols-3">
                {[
                  { key: "isPublished", label: "Published" },
                  { key: "isFeatured", label: "Featured" },
                  { key: "notifySubscribers", label: "Send email" },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(form[item.key])}
                      onChange={(event) => setForm((current) => ({ ...current, [item.key]: event.target.checked }))}
                    />
                    {item.label}
                  </label>
                ))}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded bg-gray-900 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-70"
              >
                {submitting ? "Saving..." : editingId ? "Update newsletter" : "Create newsletter"}
              </button>

              {statusMessage ? <p className="text-sm text-gray-600">{statusMessage}</p> : null}
            </div>
          </form>

          <div className="space-y-6">
            <section className="rounded border border-gray-300 bg-white p-5 sm:p-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Newsletters</h2>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                {newsletters.map((newsletter) => (
                  <div key={newsletter._id} className="rounded border border-gray-300 bg-gray-50 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-gray-600">
                          <span>{newsletter.category}</span>
                          <span>{newsletter.isPublished ? "Published" : "Draft"}</span>
                          {newsletter.isFeatured ? <span>Featured</span> : null}
                        </div>
                        <h3 className="mt-2 text-lg font-semibold text-gray-900">{newsletter.title}</h3>
                        <p className="mt-1.5 max-w-2xl text-sm text-gray-600">{newsletter.excerpt}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(newsletter)}
                          className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteNewsletter(newsletter._id)}
                          className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {!newsletters.length ? <p className="text-sm text-gray-500">No newsletters created yet.</p> : null}
              </div>
            </section>

            <section className="rounded border border-gray-300 bg-white p-5 sm:p-6">
              <h2 className="text-xl font-semibold text-gray-900">Subscribers</h2>
              <div className="mt-5 space-y-2.5">
                {subscribers.map((subscriber) => (
                  <div key={subscriber._id} className="flex flex-col gap-2 rounded border border-gray-300 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{subscriber.name || "Subscriber"}</div>
                      <div className="text-xs text-gray-500 sm:text-sm">{subscriber.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteSubscriber(subscriber._id)}
                      className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {!subscribers.length ? <p className="text-sm text-gray-500">No subscribers yet.</p> : null}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer onContactClick={() => window.location.assign("/")} />
    </div>
  );
};

export default AdminNewsletterPage;
